import React, { useState } from "react";
import { Menu, Button, Modal, Stack, Text, Loader, Alert } from "@mantine/core";
import {
  IconWand,
  IconQuestionMark,
  IconCopyCheck,
  IconFlask,
  IconChevronDown,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import api from "../../../../services/api";

interface Props {
  menuId: string;
}

type RunningJob = "analysis" | "faqs" | "test-faq" | "dedupe" | null;

export const BulkActionsMenu: React.FC<Props> = ({ menuId }) => {
  const [running, setRunning] = useState<RunningJob>(null);
  const [confirmKind, setConfirmKind] = useState<
    "analysis" | "faqs" | null
  >(null);
  const [testFaqResult, setTestFaqResult] = useState<unknown | null>(null);
  const [testFaqOpen, setTestFaqOpen] = useState(false);

  const run = async (kind: "analysis" | "faqs") => {
    setRunning(kind);
    try {
      const url =
        kind === "analysis"
          ? `/menus/${menuId}/analysis`
          : `/menus/${menuId}/faqs`;
      await api.post(url, {});
      notifications.show({
        color: "green",
        title: kind === "analysis" ? "Analysis queued" : "FAQs queued",
        message: "Items will update as the job completes.",
      });
    } catch (e: any) {
      notifications.show({
        color: "red",
        title: "Failed",
        message: e?.response?.data?.error || e?.message || "Unknown error",
      });
    } finally {
      setRunning(null);
      setConfirmKind(null);
    }
  };

  const runTestFaq = async () => {
    setRunning("test-faq");
    setTestFaqOpen(true);
    setTestFaqResult(null);
    try {
      const res = await api.post(`/menus/${menuId}/faqs/test`, {
        dryRun: true,
      });
      setTestFaqResult(res.data);
    } catch (e: any) {
      setTestFaqResult({
        error: e?.response?.data?.error || e?.message || "Unknown error",
      });
    } finally {
      setRunning(null);
    }
  };

  return (
    <>
      <Menu shadow="md" position="bottom-end">
        <Menu.Target>
          <Button
            variant="default"
            leftSection={<IconWand size={16} />}
            rightSection={<IconChevronDown size={14} />}
          >
            Bulk actions
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>AI</Menu.Label>
          <Menu.Item
            leftSection={<IconWand size={14} />}
            onClick={() => setConfirmKind("analysis")}
          >
            Redo analysis on all items
          </Menu.Item>
          <Menu.Item
            leftSection={<IconQuestionMark size={14} />}
            onClick={() => setConfirmKind("faqs")}
          >
            Generate FAQs for all items
          </Menu.Item>
          <Menu.Item
            leftSection={<IconFlask size={14} />}
            onClick={runTestFaq}
          >
            Generate test FAQ (preview)
          </Menu.Item>
          <Menu.Divider />
          <Menu.Label>Cleanup</Menu.Label>
          <Menu.Item
            leftSection={<IconCopyCheck size={14} />}
            component="a"
            href={`/admin/menus/edit/${menuId}#dedupe`}
          >
            Open dedupe review (legacy)
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      <Modal
        opened={confirmKind !== null}
        onClose={() => setConfirmKind(null)}
        title={
          confirmKind === "analysis"
            ? "Redo analysis on all items?"
            : "Generate FAQs for all items?"
        }
      >
        <Stack>
          <Text size="sm" c="dimmed">
            This runs an AI job over every item on the menu and may take a few
            minutes. You can keep editing while it runs; results will appear as
            items update.
          </Text>
          <Button
            color="orange"
            loading={running !== null}
            onClick={() => confirmKind && run(confirmKind)}
          >
            Run
          </Button>
        </Stack>
      </Modal>

      <Modal
        opened={testFaqOpen}
        onClose={() => setTestFaqOpen(false)}
        title="Test FAQ preview"
        size="lg"
      >
        {running === "test-faq" || !testFaqResult ? (
          <Stack align="center" py="xl">
            <Loader />
            <Text size="sm" c="dimmed">
              Generating preview…
            </Text>
          </Stack>
        ) : (testFaqResult as any).error ? (
          <Alert color="red">{(testFaqResult as any).error}</Alert>
        ) : (
          <pre
            style={{
              fontSize: 12,
              maxHeight: 400,
              overflow: "auto",
              background: "var(--mantine-color-gray-0)",
              padding: 12,
              borderRadius: 6,
            }}
          >
            {JSON.stringify(testFaqResult, null, 2)}
          </pre>
        )}
      </Modal>
    </>
  );
};
